<template>
  <div
    id="vue-app"
    class="app-container"
    :class="{ offline: !isOnline }"
  >
    <q-layout view="hHh LPR lFf">
      <q-header :reveal="$q.screen.lg.sm">
        <ReNavbar
          ref="navbar"
          v-model:menu-open="isMobileMenuOpen"
          :has-side-menu="canShowSideMenu"
          :fixed="$route.name === 'report'"
        />
      </q-header>

      <ReSidemenu
        v-if="canShowSideMenu"
        v-model:menu-open="isMobileMenuOpen"
        class="app-sidemenu"
      />

      <q-page-container class="app-content">
        <router-view :key="$route.query.resetCurRequest ?? false" />

        <div class="errors-container print-hide">
          <ErrorsBanner />

          <ErrorsBanner error-type="warning" />
        </div>

        <HubspotChat v-if="$config.ENABLE_HUBSPOT" />

        <ContactForm
          v-if="curUser?.authenticated.value && isContactFormActive"
          ref="contactFormRef"
          v-model="isContactFormActive"
          :module="valuation_type"
          :valuation-request-ref="valuation_request_ref"
          wide-dialog
          has-close-button
        />
      </q-page-container>

      <div id="talkjs-container" />
    </q-layout>
  </div>
</template>

<script>
import { onlineManager, useMutation } from '@tanstack/vue-query'
import { useHead } from '@unhead/vue'
import { nextTick, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import ErrorsBanner from '@/apps/MainApp/components/ErrorsBanner.vue'
import HubspotChat from '@/apps/MainApp/components/HubspotChat.vue'
import ReNavbar from '@/apps/MainApp/components/ReNavbar.vue'
import ReSidemenu from '@/apps/MainApp/components/Sidemenu/ReSidemenu.vue'
import ContactForm from '@/apps/MainApp/views/support/ContactForm.vue'
import {
  queryKeyToUrl,
  ReApi,
  ReApiVersion,
  ReEndpoint,
  SUBMIT_STORE_MUTATION_PREFIX,
  submitStoredMutations,
} from '@/composables/query'
import { useConfig } from '@/composables/useConfig'
import { useProvideRequestDocumentDataStore } from '@/composables/useProvideRequestDocumentDataStore'
import { useRequest } from '@/composables/useRequest'
import useTracker from '@/composables/useTracker'
import { useOnline } from '@/composables/utils/online'
import { contactFormKey, curRequestKey, talkjsKey, trackerKey } from '@/keys'
import axios from '@/plugins/axios'
import store from '@/store'
import { Actions } from '@/store/Auth'

import { useCurUser } from './composables/useCurUser'
import useTalkjs from './composables/useTalk'

export default {
  components: {
    ReNavbar,
    ReSidemenu,
    HubspotChat,
    ErrorsBanner,
    ContactForm,
  },

  setup() {
    const { isOnline } = useOnline()
    const config = useConfig()
    const route = useRoute()

    const tracker = useTracker()
    // TODO: move the following to a Vue-Router guard:
    if (config.ENABLE_PLAUSIBLE) {
      watch(
        () => route.fullPath,
        () => tracker.trackPageview(),
        { immediate: true },
      )
    }

    provide(trackerKey, tracker)

    const { mutateAsync } = useMutation({
      mutationKey: [SUBMIT_STORE_MUTATION_PREFIX],
    })

    // Submit stored mutations on first load, then whenever we come back online:
    // console.debug('💡 app init: submitting stored mutations')
    submitStoredMutations(mutateAsync)
    onlineManager.subscribe((_isOnline) => {
      if (_isOnline) {
        // console.debug('💡 app went online: submitting stored mutations')
        submitStoredMutations(mutateAsync)
      }
    })

    const curUser = useCurUser()

    // NOTE: some parts of the app (eg Router guards) do not have access to vars provided at this level
    // so we also keep a copy of in the store:
    store.dispatch(Actions.STORE_AUTH_USER, curUser)

    // FIXME: we shouldn't load the generic request when we are in a specific app
    const curRequest = useRequest()
    provide(curRequestKey, curRequest)
    useProvideRequestDocumentDataStore(curRequest)
    console.log(
      `🔥 app init: setting up auth user (${store.getters.getUsername}) and current request (${curRequest.requestRef})`,
    )

    const { valuation_request_ref, valuation_type } = curRequest.mapReactives(
      ['valuation_request_ref', 'valuation_type'],
      { readOnly: true },
    )

    const talkjs = useTalkjs({ curUser, curRequest })
    provide(talkjsKey, talkjs)

    const contactFormRef = ref(null)
    const isContactFormActive = ref(false)
    provide(contactFormKey, {
      open: async (opts) => {
        if (talkjs.enabled && !opts?.useLegacy)
          return talkjs.openPopup()
        isContactFormActive.value = true
        // Be sure that the component is open/mounted before calling setOptions that will overwrite props
        await nextTick()
        if (opts && contactFormRef.value)
          contactFormRef.value.setOptions(opts)

        return {
          onSend: (cb) => {
            return contactFormRef.value.subscribeOnSend(cb)
          },
        }
      },
    })

    useHead({
      title: 'Rock.estate App',
      titleTemplate: '%s | Rockestate estimator',
    })

    return {
      isOnline,
      curUser,
      curRequest,
      isContactFormActive,
      contactFormRef,
      valuation_request_ref,
      valuation_type,
      talkjs,
    }
  },

  data() {
    return {
      isMobileMenuOpen: false,
    }
  },

  computed: {
    hasWideSide() {
      return ['appDashboard'].includes(this.$route.name)
    },

    canShowSideMenu() {
      return (
        (!['login'].includes(this.$route.name)
          && !(this.$route.meta?.public || this.curUser?.authenticated.value)
          && !this.$config.CHECK_LOGIN)
        || store.state.auth.authenticated
      )
      // !this.curUser?.hasRole('borrower', 'ovm', true)
    },
  },

  created() {
    this.applyTheme()
  },

  mounted() {
    console.log(`Rockestate app ${import.meta.env.VITE_APP_VERSION_FULL}`)
    // TODO: do we still need this?
    // window.addEventListener('resize', this.onResize)

    // TODO: can we move this somewhere else? eg when doing main auth check?
    // TODO: alternatively: use a dedicated endpoint instead of /user?
    // Check that the frontend verion matches what the backend thinks it should be
    // If it doesn't, refresh with the url containing the version number
    // This should defeat aggressive corporate caches.
    if (this.$config.AUTOREFRESH_STALE_FRONTEND && this.isOnline) {
      const { url } = queryKeyToUrl([ReApi.AUTH, ReApiVersion.CURRENT, ReEndpoint.USER])
      axios
        .get(url, {
          params: { frontend_version: import.meta.env.VITE_APP_VERSION_FULL },
        })
        .then((response) => {
          const frontend_version = response.data.frontend_version
          if (
            frontend_version
            // Using numeric localCompare to get v1.2.0-8 < v1.2.0-10 < v2 < v10

            && frontend_version.localeCompare(import.meta.env.VITE_APP_VERSION_FULL, 'en', {
              numeric: true,
            }) === 1
          ) {
            console.warn(
              `current frontend version ${import.meta.env.VITE_APP_VERSION_FULL}`
              + ` expecting ${frontend_version}`,
            )
            if (!window.location.toString().includes(frontend_version)) {
              console.warn(` ==> refreshing`)
              window.location.replace(
                [location.protocol, '//', location.host, location.pathname, '?v=', frontend_version].join(''), // https://stackoverflow.com/a/5817566
              )
            }
            else {
              console.warn(' version mismatch, even after refresh')
            }
          }
        })
    }
  },

  methods: {
    applyTheme() {
      const queryString = window.location.search
      const urlParams = new URLSearchParams(queryString)

      if (urlParams.get('static')) {
        console.debug('Skipping theme application for report')
        delete this.$config.THEME_ID
        delete this.$config.THEME_COLORS
        delete this.$config.THEME_VARS
        return
      }

      try {
        if (this.$config.THEME_COLORS) {
          for (const [key, value] of Object.entries(this.$config.THEME_COLORS)) {
            document.body.style.setProperty(`--${key}`, value)
          }
        }
      }
      catch (error) {
        console.warn('Error: Couldn\'t apply theme colors: ', this.$config.THEME_COLORS)
        console.warn(error)
      }

      try {
        if (this.$config.THEME_VARS) {
          for (const [key, value] of Object.entries(this.$config.THEME_VARS)) {
            document.body.style.setProperty(`--${key}`, value)
          }
        }
      }
      catch (error) {
        console.warn('Error: Couldn\'t apply theme filters: ', this.$config.THEME_VARS)
        console.warn(error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
#vue-app {
  print-color-adjust: exact;
  -webkit-print-color-adjust: exact;
}

.app-container {
  @media print {
    overflow: visible;
    display: block;
  }

  .app-content {
    @media print {
      overflow: visible;
      display: block;
      padding-top: 0 !important;
      margin-top: 0;
      padding-left: 0 !important;
      margin-left: 0;
    }

    .errors-container {
      max-height: 40vh;
      width: 90%;
      overflow: scroll;
      position: fixed;
      bottom: 0;
      z-index: 1001;
    }
  }

  .app-sidemenu {
    @media print {
      display: none;
    }
  }
}
</style>
